import { ReactNode, useMemo } from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

export type AutocompleteItem = {
  id: string;
  icon?: ReactNode;
  title: string;
  description?: string;
  value: string;
  valueLocalized: string;
  valueCurrentLocale: string;
  regionName?: string;
  cityName?: string;
  districtName?: string;
  microdistrictName?: string;
  streetName?: string;
  metroStationName?: string;
};

interface AutocompleteListProps {
  items: AutocompleteItem[];
  searchValue: string;
  listTitle: string;
  onItemClick: (item: AutocompleteItem) => void;
  noResultsMessage?: ReactNode;
}

interface IHighlightMatchingText {
  matchLength: number;
  item: ReactNode;
}

// Helper function to highlight matching text
const highlightMatchingText = (
  text: string,
  searchValue: string,
): IHighlightMatchingText => {
  const defaultResult: IHighlightMatchingText = {
    matchLength: 0,
    item: <>{text}</>,
  };

  const normalizedSearch = searchValue.trim().toLowerCase();

  if (!normalizedSearch) return defaultResult;

  const lowerText = text.toLowerCase();
  const matchIndex = lowerText.indexOf(normalizedSearch);

  if (matchIndex === -1) return defaultResult;

  const beforeMatch = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + normalizedSearch.length);
  const afterMatch = text.slice(matchIndex + normalizedSearch.length);

  return {
    matchLength: match.length,
    item: (
      <>
        {beforeMatch}
        <span className="text-red font-bold">{match}</span>
        {afterMatch}
      </>
    ),
  };
};

export const AutocompleteList: React.FC<AutocompleteListProps> = ({
  items,
  searchValue,
  listTitle,
  onItemClick,
  noResultsMessage,
}: AutocompleteListProps) => {
  const t = useTranslations();

  const sortedItems = useMemo(() => {
    return items
      .map((item) => {
        const titleHighlight = highlightMatchingText(item.title, searchValue);

        return {
          ...item,
          _highlightTitle: titleHighlight.item,
          _matchLengthTitle: titleHighlight.matchLength,
        };
      })
      .sort((a, b) => b._matchLengthTitle - a._matchLengthTitle);
  }, [items, searchValue]);

  if (items.length === 0) {
    return (
      noResultsMessage || (
        <div className="p-4 text-default-500">
          {t("search.table.noResults")}
        </div>
      )
    );
  }

  return (
    <>
      <div className="px-[3.2rem] py-6 text-md font-bold text-[#323232] border-b-1">
        {listTitle}
      </div>
      <div className="divide-y divide-default-100 px-7 py-6 md:max-h-[600px] overflow-y-auto">
        {sortedItems.map((item) => (
          <button
            key={item.id}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onItemClick(item)}
            className={cn(
              "flex items-center gap-3 w-full text-left px-4 py-3 hover:bg-default-100 transition-colors",
              searchValue.trim().toLowerCase() === item.title.trim().toLowerCase() && "bg-default-100",
            )}
          >
            {item.icon && <div>{item.icon}</div>}
            <div>
              <div className="font-medium">{item._highlightTitle}</div>
              <div className="text-sm text-default-500">{item.description}</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
};
